// OnboardingOrchestrator — full-screen wizard rendered above the route tree.
//
// Mounts when `shouldRun` is true (gate logic in useOnboardingGate). Renders
// one slide at a time with framer-motion slide transitions; back/forward
// direction is tracked by the context so the animation can match.
//
// Role-aware step lists live in useOnboarding.ts (getStepsForRole). The
// orchestrator picks the slide component for the current step name.

import { AnimatePresence, motion } from 'framer-motion'
import { OnboardingProvider, useOnboarding, type SlideName } from './useOnboarding'
import SplashSlide from './slides/SplashSlide'
import LanguageSlide from './slides/LanguageSlide'
import WelcomeSlide from './slides/WelcomeSlide'
import NotifyPermissionSlide from './slides/NotifyPermissionSlide'
import ReadySlide from './slides/ReadySlide'
import PlaceholderSlide from './slides/PlaceholderSlide'

type Role = 'CLIENT' | 'WORKER' | 'COMPANY' | 'ADMIN' | 'GLOBAL_ADMIN'

interface Props {
  role: Role | null | undefined
  userId: string
  onCompleted: () => void
}

export default function OnboardingOrchestrator(props: Props) {
  return (
    <OnboardingProvider {...props}>
      <Inner />
    </OnboardingProvider>
  )
}

function Inner() {
  const { current, step, steps, direction, prev } = useOnboarding()
  const progress = (step + 1) / steps.length
  const onBack = step > 0 ? prev : undefined

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={current}
        initial={{ x: direction === 'forward' ? 30 : -30, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: direction === 'forward' ? -30 : 30, opacity: 0 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        className="fixed inset-0 z-[80]"
      >
        {renderSlide(current, { progress, onBack })}
      </motion.div>
    </AnimatePresence>
  )
}

function renderSlide(name: SlideName, ctx: { progress: number; onBack?: () => void }) {
  switch (name) {
    case 'splash':
      return <SplashSlide />
    case 'language':
      return <LanguageSlide progress={ctx.progress} />
    case 'welcome':
      return <WelcomeSlide progress={ctx.progress} onBack={ctx.onBack ?? (() => {})} />
    case 'notify':
      return <NotifyPermissionSlide progress={ctx.progress} onBack={ctx.onBack ?? (() => {})} />
    case 'client-address':
      return (
        <PlaceholderSlide
          progress={ctx.progress}
          onBack={ctx.onBack ?? (() => {})}
          title="Adresa ta principală"
          body="Adaugă o adresă pentru ca rezervările să meargă mai rapid."
        />
      )
    case 'client-interests':
      return (
        <PlaceholderSlide
          progress={ctx.progress}
          onBack={ctx.onBack ?? (() => {})}
          title="Servicii care te interesează"
          body="Spune-ne ce tip de servicii cauți, ca să-ți recomandăm cele mai potrivite."
        />
      )
    case 'worker-skills':
      return (
        <PlaceholderSlide
          progress={ctx.progress}
          onBack={ctx.onBack ?? (() => {})}
          title="Specializările tale"
          body="Selectează tipurile de servicii pe care le poți efectua."
        />
      )
    case 'worker-documents':
      return (
        <PlaceholderSlide
          progress={ctx.progress}
          onBack={ctx.onBack ?? (() => {})}
          title="Documente"
          body="Încarcă CI și certificările pentru verificare."
        />
      )
    case 'worker-personality':
      return (
        <PlaceholderSlide
          progress={ctx.progress}
          onBack={ctx.onBack ?? (() => {})}
          title="Test personalitate"
          body="Te ajutăm să primești joburi potrivite stilului tău."
        />
      )
    case 'company-profile':
      return (
        <PlaceholderSlide
          progress={ctx.progress}
          onBack={ctx.onBack ?? (() => {})}
          title="Datele companiei"
          body="Confirmă CUI, sediu și numele afișat."
        />
      )
    case 'company-team':
      return (
        <PlaceholderSlide
          progress={ctx.progress}
          onBack={ctx.onBack ?? (() => {})}
          title="Echipa"
          body="Invită primii tăi lucrători. Poți face asta și mai târziu."
        />
      )
    case 'company-services':
      return (
        <PlaceholderSlide
          progress={ctx.progress}
          onBack={ctx.onBack ?? (() => {})}
          title="Servicii oferite"
          body="Marchează tipurile de servicii pe care compania ta le efectuează."
        />
      )
    case 'ready':
      return <ReadySlide progress={ctx.progress} onBack={ctx.onBack ?? (() => {})} />
  }
}
